import { useState, useEffect } from 'react';
import { notify } from '../utils/toast';
import { storage } from '../lib/storage';
import type { Suggestion } from '../lib/storage/suggestions';

export const useSuggestions = () => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const loadSuggestions = async () => {
      try {
        const storedSuggestions = await storage.getSuggestions();
        if (mounted) {
          setSuggestions(storedSuggestions);
        }
      } catch (error) {
        console.error('Error loading suggestions:', error);
        if (mounted) {
          notify.error('Failed to load suggestions');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadSuggestions();

    return () => {
      mounted = false;
    };
  }, []);

  const updateSuggestion = async (id: string, content: string) => {
    if (!content.trim()) {
      notify.error('Suggestion cannot be empty');
      return;
    }

    try {
      setSuggestions(prev => 
        prev.map(suggestion => 
          suggestion.id === id 
            ? { ...suggestion, content }
            : suggestion
        )
      );

      await storage.updateSuggestion(id, content);
      setEditing(null);
      notify.success('Suggestion updated');
    } catch (error) {
      console.error('Error updating suggestion:', error);
      notify.error('Failed to update suggestion');
      
      const storedSuggestions = await storage.getSuggestions();
      setSuggestions(storedSuggestions);
    }
  };

  return {
    suggestions,
    loading,
    editing,
    setEditing,
    updateSuggestion
  };
};